import moment from "moment";
import IOrder from "../../../models/IOrder";
import { toDeliveryDateKey } from "../../business/BusinessSettings/DeliveryDeadlines/deliveryDeadlineUtils";

/** One distinct delivery date among the orders being routed, with its legend code (A, B, ... Z, AA, ...). */
export interface IDeliveryDateLegendEntry {
    /** YYYY-MM-DD */
    date: string;
    code: string;
    orderCount: number;
}

export interface IDeliveryDateLegend {
    entries: IDeliveryDateLegendEntry[];
    /** Legend code by delivery date key, for the order rows. */
    codeByDate: Map<string, string>;
    /** Orders with no delivery date at all. */
    undatedCount: number;
}

/** A, B, ... Z, AA, AB, ... for the nth (0-based) distinct date. */
export const legendCode = (index: number): string => {
    let code = "";
    let n = index;
    do {
        code = String.fromCharCode(65 + (n % 26)) + code;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return code;
};

/**
 * The distinct delivery dates among the orders, earliest first, each with a legend code, so the Create
 * Daily Route sidebar can show which orders deliver when.
 */
export const buildDeliveryDateLegend = (orders: Pick<IOrder, "delivery_date">[]): IDeliveryDateLegend => {
    const counts = new Map<string, number>();
    let undatedCount = 0;

    for (const order of orders) {
        const key = toDeliveryDateKey(order.delivery_date);
        if (key) {
            counts.set(key, (counts.get(key) || 0) + 1);
        } else {
            undatedCount++;
        }
    }

    const dates = Array.from(counts.keys()).sort();
    const codeByDate = new Map<string, string>();
    const entries = dates.map((date, index) => {
        const code = legendCode(index);
        codeByDate.set(date, code);
        return { date, code, orderCount: counts.get(date) || 0 };
    });

    return { entries, codeByDate, undatedCount };
};

/**
 * The route date to start from: the orders' shared delivery date when they all agree, today when none of
 * them carries a delivery date (the pre-existing default), and blank when they span several dates so the
 * user picks one from the legend.
 */
export const defaultRouteDate = (legend: IDeliveryDateLegend, today: moment.Moment = moment()): string => {
    if (legend.entries.length === 1) {
        return legend.entries[0].date;
    }
    if (legend.entries.length === 0) {
        return today.format("YYYY-MM-DD");
    }
    return "";
};

/** Display form of a legend date, e.g. "Wed, Nov 4". Delivery dates are calendar dates (UTC midnight), never shifted. */
export const formatLegendDate = (date: string): string => moment.utc(date, "YYYY-MM-DD").format("ddd, MMM D");
